import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";
import { ModalBase, ModalButtonProps } from "components/modal/ModalBase";
import styles from "./index.module.scss";
import { Trans, useTranslation } from "react-i18next";
import {
    CloudflareVideo,
    CreatorProductVideoPlayerEntitlement,
    CreatorProductVideoPlayerPlaylistBroadcastEntitlement,
    EntitySummaryResponse,
    VideoPlayer
} from "@switcherstudio/switcher-api-client";
import { BroadcastResponse } from "@switcherstudio/api-core-client";
import { AddVideosContent } from "components/add-videos/AddVideosContent";
import { useAddVideosActions } from "components/add-videos/useAddVideoActions";
import {
    BroadcastResponseDetails,
    BroadcastResponseDetailsProps
} from "components/entity-details/BroadcastResponseDetails";
import {
    ComponentItem,
    ActionsBarOptions
} from "components/generic-multiselect/types";
import { useGenericMultiSelect } from "components/generic-multiselect/useGenericMultiSelect";
import { useGetPagedBroadcastByFilter } from "hooks/useGetPagedBroadcastByFilter";
import { useUserStatus } from "hooks/useUserStatus";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import { v4 as uuidv4 } from "uuid";
import { getBroadcastEntitlements } from "helpers/broadcastHelpers";
import { Tabs } from "components/tabs/Tabs";
import { Spinner } from "components/spinners/Spinner";

export enum VideoFilters {
    VideoLibrary = "video-library",
    Scheduled = "scheduled"
}

export interface AddVideosModalProps {
    playerId: string;
    onSelect: (broadcastIds: string[]) => void;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    modalTitle?: string;
    onClose?: (params: any) => any;
    isOpen: boolean;
}

export interface BroadcastResponsesHashItem {
    broadcast: BroadcastResponse;
    players?: VideoPlayer[];
    playlistBroadcastEntitlements?: CreatorProductVideoPlayerPlaylistBroadcastEntitlement[];
    playerEntitlements?: CreatorProductVideoPlayerEntitlement[];
    videoResponse: CloudflareVideo;
    subsequentVideoResponses: CloudflareVideo[];
    metrics: EntitySummaryResponse;
    selected: boolean;
    disabled: boolean;
}

export const AddVideosModal = ({
    playerId,
    isOpen,
    setIsOpen,
    onSelect,
    onClose,
    modalTitle
}: AddVideosModalProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState<VideoFilters>(
        VideoFilters.VideoLibrary
    );
    const [uploadSessionId, setUploadSessionId] = useState<string>(uuidv4());
    const [uploadModalKey, setUploadModalKey] = useState<number>(0);
    const { userHasNoSubscription } = useUserStatus();

    const {
        existingBroadcasts: availableBroadcasts,
        scheduledBroadcasts,
        firstLoading: existingFirstLoading,
        reloading: existingReloading,
        refetchVideos: existingRefetchVideos,
        error: existingError,
        getNextPage: existingGetNextPage,
        allLoaded: existingAllLoaded,
        fetchBroadcastsWithNewParams: fetchExistingBroadcastsWithNewParams
    } = useGetPagedBroadcastByFilter({
        preFetch: () => {
            setUploadModalKey((prev) => prev + 1);
        },
        videoStatusType: "Library",
        itemsPerPage: 15,
        excludedPlayerIds: [playerId]
    });

    const {
        upcomingLivestreams,
        firstLoading: livestreamFirstLoading,
        refetchVideos: livestreamRefetchVideos,
        error: livestreamError,
        getNextPage: livestreamGetNextPage,
        allLoaded: livestreamAllLoaded,
        fetchBroadcastsWithNewParams: fetchLivestreamsWithNewParams
    } = useGetPagedBroadcastByFilter({
        preFetch: () => {
            setUploadModalKey((prev) => prev + 1);
        },
        videoStatusType: "Livestream",
        itemsPerPage: 15,
        excludedPlayerIds: [playerId]
    });

    const existingBroadcasts = useMemo(() => {
        return [...availableBroadcasts, ...scheduledBroadcasts];
    }, [availableBroadcasts, scheduledBroadcasts]);

    /// EXISTING
    const {
        handleDelete: existingHandleDelete,
        actionsBarOptions: existingActionBarOptions,
        searchSortOptions: existingSearchSortOptions
    } = useAddVideosActions({
        refetchVideos: existingRefetchVideos,
        isVideoLibrary: false,
        enableDelete: false
    });

    const existingBroadcastListItemComponentItems: ComponentItem<BroadcastResponse>[] =
        useMemo(() => {
            if (!existingBroadcasts.length) return [];

            return existingBroadcasts.map((broadcast) => {
                const entitlements = getBroadcastEntitlements(broadcast);

                return {
                    id: broadcast.Details.Id,
                    component: (
                        <BroadcastResponseDetails
                            broadcast={broadcast}
                            badges
                            entitlements={entitlements}
                            showPricing
                            showDownload
                            allowVideoPlaybackOnThumbnailClick
                            location="video-library"
                            handleDeleteBroadcast={() => {
                                existingHandleDelete(broadcast, entitlements);
                            }}
                            onBroadcastUpdate={() => {
                                existingRefetchVideos();
                            }}
                            disabled={broadcast.Videos?.some((v) => !v)}
                        />
                    ),
                    baseObject: broadcast
                };
            });
        }, [existingBroadcasts, existingRefetchVideos, existingHandleDelete]);

    const {
        GenericMultiSelectComponent: ExistingGenericMultiSelectComponent,
        handleSubmit: existingHandleSubmit,
        anyNewSelected: existingAnyNewSelected,
        selectedIdSet: existingSelectedIdSet,
        passthroughError: existingPassthroughError,
        setPassthroughError: setExistingPassthroughError
    } = useGenericMultiSelect<BroadcastResponse, BroadcastResponseDetailsProps>(
        {
            isMultiple: true,
            checkBoxLocation: "right",
            allowItemClick: true,
            items: existingBroadcastListItemComponentItems,
            onSubmit: (ids) => {
                if (onSelect && setIsOpen) {
                    onSelect(ids);
                    setIsOpen(false);
                }
            },
            emptyStateCopy: t("video-library:no-videos-match"),
            actionsBarOptions:
                existingActionBarOptions as ActionsBarOptions<BroadcastResponseDetailsProps>,
            searchSortOptions: existingSearchSortOptions,
            loading: existingFirstLoading,
            supressNotifications: true,
            scrollable: !existingAllLoaded,
            fetchWithNewParams: fetchExistingBroadcastsWithNewParams
        }
    );

    const ExistingAddVideosContentComponent = useMemo(
        () => (
            <AddVideosContent
                isVideoLibrary={false}
                refetchVideos={existingRefetchVideos}
                firstLoading={existingFirstLoading}
                reloading={existingReloading}
                GenericMultiSelectComponent={
                    ExistingGenericMultiSelectComponent
                }
                t={t}
                userHasNoSubscription={userHasNoSubscription}
                videoUploadSessionId={uploadSessionId}
                setVideoUploadSessionId={setUploadSessionId}
                playerId={playerId}
                uploadModalKey={uploadModalKey}
                onClose={onClose}
            />
        ),
        [
            existingRefetchVideos,
            existingFirstLoading,
            existingReloading,
            ExistingGenericMultiSelectComponent,
            t,
            userHasNoSubscription,
            uploadSessionId,
            setUploadSessionId,
            playerId,
            uploadModalKey,
            onClose
        ]
    );

    /// SCHEDULED
    const {
        handleDelete: scheduledHandleDelete,
        actionsBarOptions: scheduledActionBarOptions,
        searchSortOptions: scheduledSearchSortOptions
    } = useAddVideosActions({
        refetchVideos: livestreamRefetchVideos,
        isVideoLibrary: false,
        enableDelete: false
    });

    const scheduledBroadcastListItemComponentItems: ComponentItem<BroadcastResponse>[] =
        useMemo(() => {
            if (!upcomingLivestreams.length) return [];

            return upcomingLivestreams.map((broadcast) => {
                const entitlements = getBroadcastEntitlements(broadcast);
                return {
                    id: broadcast.Details.Id,
                    component: (
                        <BroadcastResponseDetails
                            broadcast={broadcast}
                            badges
                            entitlements={entitlements}
                            showPricing
                            showEdit={false}
                            showDownload
                            allowVideoPlaybackOnThumbnailClick
                            location="video-library"
                            handleDeleteBroadcast={() => {
                                scheduledHandleDelete(broadcast, entitlements);
                            }}
                            onBroadcastUpdate={() => {
                                livestreamRefetchVideos();
                            }}
                            disabled={broadcast.Videos?.some((v) => !v)}
                        />
                    ),
                    baseObject: broadcast
                };
            });
        }, [
            upcomingLivestreams,
            livestreamRefetchVideos,
            scheduledHandleDelete
        ]);

    const {
        GenericMultiSelectComponent: ScheduledGenericMultiSelectComponent,
        handleSubmit: scheduledHandleSubmit,
        anyNewSelected: scheduledAnyNewSelected,
        selectedIdSet: scheduledSelectedIdSet,
        passthroughError: scheduledPassthroughError,
        setPassthroughError: setScheduledPassthroughError
    } = useGenericMultiSelect<BroadcastResponse, BroadcastResponseDetailsProps>(
        {
            isMultiple: true,
            checkBoxLocation: "right",
            allowItemClick: true,
            items: scheduledBroadcastListItemComponentItems,
            onSubmit: (ids) => {
                if (onSelect && setIsOpen) {
                    onSelect(ids);
                    setIsOpen(false);
                }
            },
            emptyStateCopy: t("video-library:no-videos-match"),
            actionsBarOptions:
                scheduledActionBarOptions as ActionsBarOptions<BroadcastResponseDetailsProps>,
            searchSortOptions: scheduledSearchSortOptions,
            loading: livestreamFirstLoading,
            supressNotifications: true,
            scrollable: !livestreamAllLoaded,
            fetchWithNewParams: fetchLivestreamsWithNewParams
        }
    );

    const globalAnyNewSelected = useMemo(
        () => existingAnyNewSelected || scheduledAnyNewSelected,
        [existingAnyNewSelected, scheduledAnyNewSelected]
    );

    const globalHandleSubmit = useCallback(() => {
        existingHandleSubmit();
        scheduledHandleSubmit();
    }, [existingHandleSubmit, scheduledHandleSubmit]);

    useEffect(() => {
        if (existingPassthroughError || scheduledPassthroughError) {
            dispatch(
                addNotification({
                    type: NotificationType.Danger,
                    message: t("errors:no-items-selected")
                })
            );

            setExistingPassthroughError(null);
            setScheduledPassthroughError(null);
        }
    }, [
        existingPassthroughError,
        scheduledPassthroughError,
        dispatch,
        t,
        setExistingPassthroughError,
        setScheduledPassthroughError
    ]);

    const primaryButton = useMemo((): ModalButtonProps => {
        const numberCurrentlySelected =
            existingSelectedIdSet.size + scheduledSelectedIdSet.size;
        return {
            text: `${t("video-player-page:add-to-collection")} ${
                numberCurrentlySelected > 0
                    ? `(${numberCurrentlySelected})`
                    : ""
            }`,
            onClick: globalHandleSubmit,
            disabled: !globalAnyNewSelected
        };
    }, [
        globalAnyNewSelected,
        globalHandleSubmit,
        t,
        existingSelectedIdSet,
        scheduledSelectedIdSet
    ]);

    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            const target = e.target as HTMLDivElement;
            // Check if the user has scrolled near the bottom of the list
            if (
                target.scrollTop + target.clientHeight >=
                target.scrollHeight - 100
            ) {
                if (activeTab === VideoFilters.VideoLibrary) {
                    existingGetNextPage();
                } else {
                    livestreamGetNextPage();
                }
            }
        },
        [activeTab, existingGetNextPage, livestreamGetNextPage]
    );

    const content = useMemo(() => {
        // Render
        if (existingFirstLoading || livestreamFirstLoading) {
            return (
                <div className={styles["loading-wrapper"]}>
                    <Spinner />
                </div>
            );
        }

        if (existingError || livestreamError) {
            return (
                <div>
                    Error: {existingError?.message || livestreamError?.message}
                </div>
            );
        }
        return (
            <div
                className={`content ${styles["content"]}`}
                onScroll={handleScroll}
            >
                <Tabs<VideoFilters>
                    tabs={[
                        {
                            id: VideoFilters.VideoLibrary,
                            label: t("page-titles:video-library"),
                            component: ExistingAddVideosContentComponent
                        },
                        {
                            id: VideoFilters.Scheduled,
                            label: t("collection-page:scheduled-livestreams"),
                            component: (
                                <>
                                    <p
                                        className={
                                            styles["schedule-livestream-cta"]
                                        }
                                    >
                                        <Trans
                                            i18nKey={
                                                "video-player-page:schedule-livestream-cta"
                                            }
                                            components={{
                                                link1: (
                                                    <Link
                                                        to={
                                                            "https://itunes.apple.com/us/app/switcher-studio-pro/id908386221"
                                                        }
                                                        target="_blank"
                                                    />
                                                )
                                            }}
                                        />
                                    </p>
                                    {ScheduledGenericMultiSelectComponent}
                                </>
                            )
                        }
                    ]}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                />
            </div>
        );
    }, [
        ExistingAddVideosContentComponent,
        ScheduledGenericMultiSelectComponent,
        activeTab,
        existingFirstLoading,
        livestreamFirstLoading,
        existingError,
        livestreamError,
        t,
        handleScroll
    ]);

    return (
        <div className={`${styles["add-videos-modal"]} add-videos-modal`}>
            <ModalBase
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                header={modalTitle || t("video-player-page:add-videos")}
                dismissButton={t("buttons:cancel")}
                onDismiss={() => setIsOpen(false)}
                primaryButton={primaryButton}
            >
                {content}
            </ModalBase>
        </div>
    );
};
